<template>
  <div class="create-sale">
      <template v-if="accountTypeI !== ACCOUNT_TYPES.syndicate" class="container container_40">
        <not-available-card icon='work'
                            :title="i18n.lbl_not_available()"
                            :descr="i18n.sale_not_available_exp()"/>
      </template>

      <template v-else-if="view.mode === VIEW_MODES.edit">

        <div class="container-header">
          <div class="container-header__title">{{ i18n.lbl_creation_sale() }}</div>
        </div>

        <md-steppers md-linear
                     md-dynamic-height
                     :md-active-step.sync="activeStep">
          <md-step v-for="(step, i) in steps"
                   :key="i"
                   :id="step.name"
                   :md-label="step.label"
                   :md-done.sync="step.done"
          >
            <component :is="step.component"
                       :schema="step.schema"
                       :sale="sale"
                       :token="token"
                       @sale-update="handleSaleUpdate($event, { step, i })"
                       @token-save="handleTokenSave($event, { step, i })"
                       @step-back="stepBack({ step, i })"
                       @sale-edit-end="handleSaleEditEnd"
                       @progres-update="progressCounter(steps)"
            />
          </md-step>
        </md-steppers>
      </template>

      <template v-else-if="view.mode === VIEW_MODES.list">
        <div class="container-header">
          <div class="container-header__title">{{ i18n.sale_open_requests() }}</div>
          <md-button class="btn btn_wide md-primary" @click="startNewSale">{{ i18n.sale_start_new_sale() }}</md-button>
        </div>
        <request-list :list="listManager.list" @sale-select="handleSaleSelect"/>
      </template>

      <template v-else-if="view.mode === VIEW_MODES.status">
        <div class="container-header">
          <div class="container-header__title">{{ i18n.lbl_creation_sale() }}</div>
        </div>

        <div class="container container_40">
            <div class="text_x_large">{{ i18n.sale_status_title() }}</div>
            <div class="text_large text_description text_margin">{{ i18n.sale_status_text() }}</div>
            <div class="btn-wrap column margin-top">
              <md-button class="btn md-primary" @click="$router.push('app.dashboard')">{{ i18n.lbl_to_dashboard() }}</md-button>
            </div>
        </div>
      </template>

  </div>
</template>

<script>
  import FormMixin from '@com/mixins/form.mixin'
  import RequestList from './SaleCreation.RequestList'
  import NotAvailableCard from '../common/NotAvailableCard'
  import steps from './specs/steps.schema'
  import config from '@conf'
  import { i18n } from '@js/i18n'
  import { SaleRequestRecord } from '@js/records/sale_request.record'
  import { salesService } from '@api/sales.service'
  import { TokenCreationRecord } from '@js/records/token_creation.record'
  import { tokensService } from '@api/tokens.service'
  import { SaleListManager } from './specs/sale-list-manager'
  import { mapGetters, mapActions } from 'vuex'
  import { vuexTypes } from '@vuex/types'
  import { DateHelper } from '@js/helpers/date.helper'
  import { ErrorHandler } from '@js/errors/error_handler'
  import { ACCOUNT_TYPES, CREATE_TOKEN_REQUEST_STATES } from '@const'
  import { EventDispatcher } from '@js/events/event_dispatcher'
  import { reviewableRequestsService } from '@api/reviewable_requests.service'
  import _get from 'lodash/get'

  const VIEW_MODES = {
    list: 'list',
    edit: 'edit',
    confirm: 'confirm',
    status: 'status'
  }

  export default {
    name: 'CreateSale-index',
    components: { RequestList, NotAvailableCard },
    mixins: [FormMixin],
    props: ['id'],
    data: _ => ({
      activeStep: steps[0].name,
      sale: null,
      saleRequest: null,
      token: null,
      tokenRequest: null,
      listManager: new SaleListManager(),
      progress: 0,
      i18n,
      steps,
      view: {
        mode: null
      },
      VIEW_MODES,
      ACCOUNT_TYPES
    }),
    async created () {
      await Promise.all([
        this.listManager.fetch()
      ])
      if (this.id) {
        this.saleRequest = await reviewableRequestsService.loadReviewableRequestById(this.id)
      }
      if (!this.id && this.listManager.list.length) {
        this.view.mode = VIEW_MODES.list
        return
      }
      this.startNewSale()
    },

    computed: {
      ...mapGetters([
        vuexTypes.accountId,
        vuexTypes.accountTypeI,
        vuexTypes.accountOwnedTokens
      ])
    },

    methods: {
      ...mapActions({
        loadTokens: vuexTypes.GET_ALL_TOKENS,
        loadBalances: vuexTypes.GET_ACCOUNT_BALANCES,
        loadTokenList: vuexTypes.GET_USER_TOKENS_CREATION_REQUESTS
      }),
      async startNewSale () {
        const sale = new SaleRequestRecord(this.saleRequest || undefined)
        this.sale = sale

        await this.loadTokenData()

        if (!this.id) this.listManager.add(sale)
        this.view.mode = VIEW_MODES.edit
      },
      async loadTokenData () {
        let tokenListRequest = await this.loadTokenList()
        let filtered = tokenListRequest.filter(item => item.tokenCode === this.sale.baseAsset)

        if (this.id) this.tokenRequest = await reviewableRequestsService.loadReviewableRequestById(filtered[0].id)

        this.token = new TokenCreationRecord(this.tokenRequest || undefined)

        if (!this.token.policies.length) {
          this.token.policies = 1
        } else if (Array.isArray(this.token.policies)) {
          this.token.policies = (this.token.policies).reduce((a, b) => (a | b), 0)
        }
      },
      progressCounter (steps) {
        let counter = 0
        let stepsDone = 0
        let progress = 0
        const savedSale = this.sale.getDetailsForSave()
        const savedSaleSteps = savedSale.details.sale.steps
        for (let i in savedSaleSteps) {
          let currentStep = steps[counter]
          currentStep.done = false
          counter += 1
          if (savedSaleSteps[i] === true) {
            currentStep.done = true
            stepsDone += 1
            progress = (100 / (steps.length - 1)) * (stepsDone) // progress of first step must be '0', total steps length more then needed by 1 step
            this.progress = progress
          }
        }
      },
      goToList () {
        this.view.mode = VIEW_MODES.list
        this.$router.push({ path: '/requests/sale-creation' })
      },
      stepBack ({ step, i }) {
        this.activeStep = (steps[i - 1] || steps[0]).name
      },
      handleSaleSelect (sale) {
        this.sale = sale
        this.token = sale.token ? sale.token : {}
        this.view.mode = VIEW_MODES.edit
      },
      handleSaleUpdate ({ form, documents }, { step, i }) {
        form = form || {}
        documents = documents || {}
        Object.entries(form).forEach(([key, value]) => {
          this.sale[key] = value
        })
        Object.entries(documents).forEach(([key, value]) => {
          this.sale.logo = value
        })
        if (this.activeStep === steps[steps.length - 1].name) {
          return
        }
        step.done = true
        this.activeStep = (steps[i + 1] || steps[0]).name
        this.sale.steps['step_' + (i + 1)] = true
        this.progressCounter(steps)
        this.listManager.pushToStorage()
        this.scrollHandler()
      },
      handleTokenSave ({ form, documents }, { step, i }) {
        Object.entries(form).forEach(([key, value]) => {
          this.token[key] = value
        })
        this.token.logo = documents.tokenIcon
        step.done = true
        this.activeStep = (steps[i + 1] || steps[0]).name
        this.sale.steps['step_' + (i + 1)] = true
        this.sale.token = this.token
        this.listManager.pushToStorage()
        this.progressCounter(steps)
        this.scrollHandler()
      },
      async handleSaleEditEnd () {
        this.disable()
        try {
          let tokenCreation = await this.createTokenRequest()
          if (tokenCreation) await this.createCampaignRequest()
        } catch (error) {
          console.log(error)
          ErrorHandler.processUnexpected(error)
        }
        this.enable()
      },
      async createTokenRequest () {
        if (this.token.state === CREATE_TOKEN_REQUEST_STATES.approved.codeVerbose || this.token.stateI === CREATE_TOKEN_REQUEST_STATES.approved.code) { // don't send TokenCreationRequest if Token was created and approved by Admin
          return true
        }
        let preissuedAssetSigner = config.NULL_ASSET_SIGNER
        let initialPreissuedAmount = this.token.maxIssuanceAmount

        try {
          await tokensService.createTokenCreationRequest({
            requestID: this.tokenRequest ? this.tokenRequest.id : '0',
            code: this.token.tokenCode,
            preissuedAssetSigner: preissuedAssetSigner,
            maxIssuanceAmount: this.token.maxIssuanceAmount,
            policies: this.token.policies || 0,
            initialPreissuedAmount: initialPreissuedAmount,
            details: {
              name: this.token.tokenName,
              logo: this.token.logo
            }
          })
          return true
        } catch (error) {
          console.error(error)
          ErrorHandler.processUnexpected(error)
          return false
        }
      },
      async createCampaignRequest () {
        await this.listManager.fetch()
        const opts = _get(this.sale.getDetailsForSave(), 'details.sale')
        this.disable()
        try {
          const saleCreationResponse = await salesService.createSaleCreationRequest({
            requestID: opts.request_id,
            baseAsset: this.token.tokenCode,
            defaultQuoteAsset: opts.default_quote_asset,
            startTime: DateHelper.toTimestamp(opts.start_time_now ? '' : opts.start_time, 'X'),
            endTime: DateHelper.toTimestamp(opts.end_time, 'X'),
            softCap: opts.soft_cap,
            hardCap: opts.hard_cap,
            details: {
              name: opts.details.name,
              internal_type: opts.details.internal_type,
              annual_percentage: opts.details.annual_percentage,
              payments_frequency: opts.details.payments_frequency,
              profitability_from: opts.details.profitability_from,
              profitability_to: opts.details.profitability_to,
              loan_term: opts.details.loan_term,
              financial_frequency: opts.details.financial_frequency,
              term: opts.details.term,
              min_term: opts.details.min_term,
              short_description: opts.details.short_description,
              description: opts.details.description,
              logo: opts.details.logo,
              youtube_video_id: opts.details.youtube_video_id,
              video_description: opts.details.video_description_array,
              min_invest_amount: opts.details.min_invest_amount,
              token_amount: this.token.maxIssuanceAmount,
              notifier: process.env.NODE_ENV === 'stage' ? 'notifier_stage' : 'notifier'
            },
            baseAssetForHardCap: this.token.maxIssuanceAmount,
            quoteAssets: opts.quote_assets,
            isCrowdfunding: true
          })
          console.log(saleCreationResponse)
          this.listManager.drop(this.sale)
          await this.listManager.fetch()
          this.view.mode = VIEW_MODES.status
          EventDispatcher.dispatchShowSuccessEvent(i18n.sale_create_request_success())
        } catch (error) {
          console.error(error)
          ErrorHandler.processUnexpected(error)
        }
        this.enable()
      },
      scrollHandler () {
        const scroller = document.getElementsByClassName('md-app-scroller')
        scroller[0].scrollTo(0, 0)
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import "steps/step";
</style>



// WEBPACK FOOTER //
// src/vue/app/saleCreation/SaleCreation.Index.vue